/**
 * My Goals State
 *
 * Manages goals for students in the current user's caseload with real-time Firestore sync.
 * Automatically initializes and updates based on AccountState caseload changes.
 *
 * @example
 * // State auto-initializes when AccountState has caseload
 * const goals = myGoalsState.data();
 * const count = myGoalsState.count();
 *
 * // Access specific goal
 * const goal = myGoalsState.getGoalById('goal-123');
 *
 * // State automatically clears when caseload is empty
 */

import { Injectable, inject, computed, signal, effect, untracked } from '@angular/core';
import { Unsubscribe } from '@angular/fire/firestore';
import { FirestoreService } from 'src/app/core/services/firestore/firestore';
import {
  AugmentedGoal,
  Goal,
  GoalObservationSchedule,
  GoalStatus,
  ScheduleEntry,
} from 'src/app/domain/goals/types/Goal.model';
import {
  addMeasurementToIndex,
  affectedPairsForMeasurement,
  augmentGoal,
  buildMeasurementIndex,
  diffMeasurementLists,
  msToIsoDate,
  MutableMeasurementIndex,
  removeMeasurementFromIndex,
  updateMeasurementInIndex,
  utcTodayMs,
  MeasurementIndex,
} from 'src/app/domain/goals/services/goal-augmentation';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import { AccountState } from 'src/app/store/account';
import { MyObservationsState } from 'src/app/store/my-observations';
import { SkillsState } from 'src/app/store/skills/skills.state';
import { MyStudentsState } from 'src/app/store/my-students';
import { MyGoalsStateModel, MyGoalsInitConfig, MY_GOALS_DEFAULTS } from './my-goals.types';

@Injectable({
  providedIn: 'root',
})
export class MyGoalsState {
  // =============================================
  // DEPENDENCIES
  // =============================================
  private readonly firestoreService = inject(FirestoreService);
  private readonly accountState = inject(AccountState);
  private readonly myObservationsState = inject(MyObservationsState);
  private readonly skillsState = inject(SkillsState);
  private readonly myStudentsState = inject(MyStudentsState);

  // =============================================
  // PRIVATE STATE (Writable Signals)
  // =============================================
  private readonly _data = signal<AugmentedGoal[]>(MY_GOALS_DEFAULTS.data);
  private readonly _initialized = signal<boolean>(MY_GOALS_DEFAULTS.initialized);
  private readonly _loading = signal<boolean>(MY_GOALS_DEFAULTS.loading);
  private readonly _error = signal<string | null>(MY_GOALS_DEFAULTS.error);
  private readonly _lastUpdated = signal<number | null>(MY_GOALS_DEFAULTS.lastUpdated);
  private readonly _lastSync = signal<number | null>(MY_GOALS_DEFAULTS.lastSync);
  private readonly _activeTeamId = signal<string | null>(MY_GOALS_DEFAULTS.activeTeamId);
  private readonly _caseload = signal<string[]>(MY_GOALS_DEFAULTS.caseload);

  // =============================================
  // PUBLIC STATE (Readonly Signals)
  // =============================================

  readonly data = this._data.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly lastUpdated = this._lastUpdated.asReadonly();
  readonly lastSync = this._lastSync.asReadonly();
  readonly activeTeamId = this._activeTeamId.asReadonly();
  readonly caseload = this._caseload.asReadonly();

  // =============================================
  // COMPUTED STATE (Derived Values)
  // =============================================

  readonly isReady = computed(() => this.initialized() && !this.loading() && !this.error());

  readonly hasData = computed(() => this.data().length > 0);

  readonly count = computed(() => this.data().length);

  /** Goals sorted by end date, most recent first */
  readonly goalsSorted = computed(() => {
    return [...this.data()].sort((a, b) => {
      const aDate = a.endDate ?? '';
      const bDate = b.endDate ?? '';
      return bDate > aDate ? 1 : bDate < aDate ? -1 : 0;
    });
  });

  /** Goals in 'draft' status — baseline data still being collected. */
  readonly draftGoals = computed(() => this.data().filter(g => g.status === 'draft'));

  /** Goals in 'active' status — targets set, progress monitoring underway. */
  readonly activeGoals = computed(() => this.data().filter(g => g.status === 'active'));

  /** Goals in 'completed' status. */
  readonly completedGoals = computed(() => this.data().filter(g => g.status === 'completed'));

  /** All non-completed goals (draft + active). */
  readonly nonCompletedGoals = computed(() => this.data().filter(g => g.status !== 'completed'));

  /** Count of active goals */
  readonly activeGoalsCount = computed(() => this.activeGoals().length);

  /** Count of completed goals */
  readonly completedGoalsCount = computed(() => this.completedGoals().length);

  /**
   * Full future observation schedule merged across all goals.
   *
   * Enumerates every scheduled slot from each goal's startDate at its
   * observationFrequency-day interval, keeping only dates on or after today.
   * Per-goal entries are kept distinct — the merge key includes `goalId` so
   * two goals on the same student tracking the same skill on the same date
   * stay separate. Keys are sorted chronologically.
   */
  readonly observationFullSchedule = computed((): GoalObservationSchedule => {
    const entryIndex = new Map<string, ScheduleEntry>();
    const byDate: Record<string, ScheduleEntry[]> = {};

    for (const goal of this.data()) {
      for (const [date, entries] of Object.entries(goal._observationFullSchedule)) {
        for (const entry of entries) {
          const key = `${date}::${entry.goalId}::${entry.studentName}::${entry.skillId}`;
          const existing = entryIndex.get(key);

          if (existing) {
            const existingTypes = new Set(existing.items.map(i => i.type));
            for (const item of entry.items) {
              if (!existingTypes.has(item.type)) existing.items.push(item);
            }
          } else {
            const copy: ScheduleEntry = { ...entry, items: [...entry.items] };
            entryIndex.set(key, copy);
            (byDate[date] ??= []).push(copy);
          }
        }
      }
    }

    return Object.fromEntries(Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)));
  });

  /**
   * Set of all `YYYY-MM-DD` date keys that appear in the full observation schedule.
   * Used by calendar components to dot/highlight days that have scheduled observations.
   */
  readonly scheduledDateKeys = computed(() => new Set(Object.keys(this.observationFullSchedule())));

  /**
   * Observation-aware observation schedule merged across all goals.
   *
   * Unlike observationFullSchedule (a static calendar grid anchored to
   * startDate), this merges each goal's _observationSchedule, where every
   * date key is computed as lastMeasurementDate + observationFrequency.
   * Collecting data today moves the date key into the future, so past-due
   * and due-today lists immediately reflect the updated state.
   *
   * Per-goal entries are kept distinct — the merge key includes `goalId` so
   * two goals on the same student tracking the same skill stay separate.
   * Keys are sorted chronologically.
   */
  readonly measurementAwareSchedule = computed((): GoalObservationSchedule => {
    const entryIndex = new Map<string, ScheduleEntry>();
    const byDate: Record<string, ScheduleEntry[]> = {};

    for (const goal of this.data()) {
      for (const [date, entries] of Object.entries(goal._observationSchedule)) {
        for (const entry of entries) {
          const key = `${date}::${entry.goalId}::${entry.studentName}::${entry.skillId}`;
          const existing = entryIndex.get(key);

          if (existing) {
            const existingTypes = new Set(existing.items.map(i => i.type));
            for (const item of entry.items) {
              if (!existingTypes.has(item.type)) existing.items.push(item);
            }
          } else {
            const copy: ScheduleEntry = { ...entry, items: [...entry.items] };
            entryIndex.set(key, copy);
            (byDate[date] ??= []).push(copy);
          }
        }
      }
    }

    return Object.fromEntries(Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)));
  });

  /**
   * Number of goals with at least one (skill, type) pair whose next scheduled
   * window has arrived today (_isDueToday === true). Collecting data today
   * immediately clears this flag via the reactive measurement index.
   */
  readonly dueTodayObservationCount = computed((): number => this.data().filter(g => g._isDueToday).length);

  /**
   * Number of goals where the observation window has been missed
   * (_isOverdue === true, i.e. daysSinceLast > observationFrequency).
   * Collecting data immediately clears this flag.
   */
  readonly overdueObservationCount = computed((): number => this.data().filter(g => g._isOverdue).length);

  // =============================================
  // PUBLIC QUERY METHODS
  // =============================================

  /** Returns all schedule entries for a given `YYYY-MM-DD` date key. */
  getEntriesForDate(dateKey: string): ScheduleEntry[] {
    return this.observationFullSchedule()[dateKey] ?? [];
  }

  getGoalById(id: string): AugmentedGoal | null {
    return this.data().find(g => g.id === id) ?? null;
  }

  /** Optimistically remove a goal from local state after a successful delete. */
  removeGoal(id: string): void {
    this._data.update(current => current.filter(g => g.id !== id));
  }

  /** Optimistically patch a goal in local state after a successful update. */
  patchGoal(id: string, updates: Partial<Goal>): void {
    const index = buildMeasurementIndex(this.myObservationsState.data());
    const skillNameMap = this.skillsState.nameMap();
    const studentNameMap = this.myStudentsState.nameMap();
    this._data.update(current =>
      current.map(g => {
        if (g.id !== id) return g;
        const merged = { ...g, ...updates };
        return augmentGoal(merged, index, skillNameMap, studentNameMap);
      }),
    );
  }

  getGoalsByStudentId(studentId: string): AugmentedGoal[] {
    return this.data().filter(g => g.studentId === studentId);
  }

  /** Filter goals by student ID and status. */
  getGoalsByStudentAndStatus(studentId: string, status: GoalStatus): AugmentedGoal[] {
    return this.data().filter(g => g.studentId === studentId && g.status === status);
  }

  /** Filter goals whose `skillArea` matches the given skill area id. */
  getGoalsBySkillAreaId(skillAreaId: string): AugmentedGoal[] {
    return this.data().filter(g => g.skillArea === skillAreaId);
  }

  /** Filter goals where the given skill ID appears in skillIds */
  getGoalsBySkillId(skillId: string): AugmentedGoal[] {
    return this.data().filter(g => g.skillIds?.includes(skillId));
  }

  /** Clear error state */
  clearError(): void {
    this._error.set(null);
  }

  // =============================================
  // PRIVATE SUBSCRIPTIONS
  // =============================================
  private _realtimeUnsubscribes: Unsubscribe[] = [];
  private _isInitializing = false;
  private _effectDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _activeProfileId: string | null = null;

  // Listener merge queue (prevents race conditions; stores raw Goal[] — enriched on dequeue)
  private _listenerMergeQueue: Array<{ batchIndex: number; goals: Goal[] }> = [];
  private _isProcessingMerge = false;

  // =============================================
  // CONSTRUCTOR - AUTO-REACTIVE INITIALIZATION
  // =============================================

  constructor() {
    effect(() => {
      const caseload = this.accountState.studentIds();
      const activeTeam = this.accountState.activeTeam();
      const activeTeamId = activeTeam?.id || null;
      const activeProfileId = this.accountState.activeProfileId();
      const profilesLoaded = this.accountState.hasProfiles();

      const prevCaseload = this._caseload();
      const prevTeamId = this._activeTeamId();
      const prevProfileId = this._activeProfileId ?? null;

      const caseloadChanged = JSON.stringify([...prevCaseload].sort()) !== JSON.stringify([...caseload].sort());
      const teamChanged = prevTeamId !== activeTeamId;
      const profileChanged = prevProfileId !== activeProfileId;

      // Case 1: Profiles not loaded yet — wait
      if (!profilesLoaded) {
        return;
      }

      // Case 2: No team — clear state
      if (!activeTeamId) {
        if (this._initialized()) {
          this.reset();
        }
        return;
      }

      // Case 3: Caseload is empty — clear state
      if (!caseload || caseload.length === 0) {
        if (this._initialized()) {
          this.reset();
        }
        return;
      }

      // Case 4: Team changed — reset and reinitialize immediately (no debounce)
      if (teamChanged && this._initialized()) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, caseload }).catch(console.error);
        return;
      }

      // Case 5: Profile changed (same team) — reset and reinitialize immediately
      if (profileChanged && this._initialized() && activeTeamId) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, caseload }).catch(console.error);
        return;
      }

      if (!this._activeProfileId) {
        this._activeProfileId = activeProfileId;
      }

      if (this._effectDebounceTimer) {
        clearTimeout(this._effectDebounceTimer);
      }

      // Cases 6 & 7: Debounce caseload changes and initial load
      this._effectDebounceTimer = setTimeout(() => {
        // Case 6: Caseload changed (same team, same profile) — reset + reload
        if (caseloadChanged && this._initialized() && !this._isInitializing) {
          this.reset();
          this.initialize({ activeTeamId, caseload }).catch(console.error);
          return;
        }

        // Case 7: Initial load
        if (!this._initialized() && !this._isInitializing && activeTeamId && caseload.length > 0) {
          this.initialize({ activeTeamId, caseload }).catch(console.error);
        }
      }, 200);
    });

    // When a student is removed from MyStudentsState, immediately drop their
    // goals from local state without waiting for the Firestore listener.
    effect(() => {
      const students = this.myStudentsState.data();
      const studentsInitialized = this.myStudentsState.initialized();
      untracked(() => {
        if (!studentsInitialized || this._data().length === 0) return;
        const studentIdSet = new Set(students.map(s => s.id).filter(Boolean));
        if (this._data().some(g => g.studentId && !studentIdSet.has(g.studentId))) {
          this._data.update(goals => goals.filter(g => !g.studentId || studentIdSet.has(g.studentId)));
        }
      });
    });

    // Re-augment goals whenever measurements change. Uses an incremental,
    // diff-based index update + selective re-augmentation so cost scales with
    // the number of *changed* measurements and *affected* goals rather than
    // the totals — critical once a team crosses ~1k goals / ~10k measurements.
    //
    // Debounced ~50ms so a burst of Firestore snapshots (common when a
    // document touches multiple buckets or a batched write fires) collapses
    // into a single augmentation pass.
    effect(() => {
      const measurements = this.myObservationsState.data();
      untracked(() => {
        this.scheduleMeasurementApply(measurements);
      });
    });
  }

  // =============================================
  // INCREMENTAL INDEX MAINTENANCE
  // =============================================

  /** The cached mutable index — reused across diffs instead of rebuilt each time. */
  private _measurementIndex: MutableMeasurementIndex | null = null;
  /** The measurements snapshot the cached index was built against. Used to diff on change. */
  private _lastMeasurementsSnapshot: Observation[] = [];
  /** Debounce timer for `scheduleMeasurementApply`. */
  private _measurementApplyTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleMeasurementApply(nextMeasurements: Observation[]): void {
    if (this._measurementApplyTimer != null) clearTimeout(this._measurementApplyTimer);
    this._measurementApplyTimer = setTimeout(() => {
      this._measurementApplyTimer = null;
      this.applyMeasurementChange(nextMeasurements);
    }, 50);
  }

  private applyMeasurementChange(next: Observation[]): void {
    const goals = this._data();
    if (goals.length === 0) {
      this._lastMeasurementsSnapshot = next;
      // Don't bother building the index until we actually have goals to augment.
      return;
    }

    const skillNameMap = this.skillsState.nameMap();
    const studentNameMap = this.myStudentsState.nameMap();

    // First measurement-driven run OR data reset: full rebuild is the right
    // call — cheaper than N individual inserts when we have no prior state.
    if (!this._measurementIndex) {
      this._measurementIndex = buildMeasurementIndex(next) as MutableMeasurementIndex;
      this._lastMeasurementsSnapshot = next;
      this._data.set(goals.map(g => augmentGoal(g, this._measurementIndex!, skillNameMap, studentNameMap)));
      return;
    }

    const { added, removed, updated } = diffMeasurementLists(this._lastMeasurementsSnapshot, next);

    // No structural change and no reference update — nothing to do. (Common
    // when unrelated signals fire that happened to re-evaluate this effect.)
    if (added.length === 0 && removed.length === 0 && updated.length === 0) {
      this._lastMeasurementsSnapshot = next;
      return;
    }

    // Apply the diff to the mutable index.
    for (const m of removed) removeMeasurementFromIndex(this._measurementIndex, m);
    for (const m of added) addMeasurementToIndex(this._measurementIndex, m);
    for (const { prev, curr } of updated) updateMeasurementInIndex(this._measurementIndex, prev, curr);

    // Collect affected (studentId, skillId, type) triples — goals untouched
    // by these pairs don't need re-augmentation.
    const affected = new Set<string>();
    const markPair = (studentId: string, skillId: string, type: string): void => {
      affected.add(`${studentId}::${skillId}::${type}`);
    };
    const markMeasurement = (m: Observation): void => {
      for (const p of affectedPairsForMeasurement(m)) markPair(p.studentId, p.skillId, p.type);
    };
    for (const m of removed) markMeasurement(m);
    for (const m of added) markMeasurement(m);
    for (const { prev, curr } of updated) {
      markMeasurement(prev);
      markMeasurement(curr);
    }

    this._lastMeasurementsSnapshot = next;

    // Re-augment only goals that touch an affected pair.
    const index = this._measurementIndex;
    this._data.set(
      goals.map(g => {
        if (!this.goalTouchesAnyPair(g, affected)) return g;
        return augmentGoal(g, index, skillNameMap, studentNameMap);
      }),
    );
  }

  /**
   * Does any of the goal's tracked / target pairs overlap the set of index
   * keys changed by the current diff? Drafts consult `trackedMeasurements`
   * (targetData is empty); active/completed goals consult `targetData`.
   */
  private goalTouchesAnyPair(g: AugmentedGoal, affected: Set<string>): boolean {
    if (g.status === 'draft') {
      for (const skillId of g.skillIds) {
        for (const type of g.trackedMeasurements[skillId] ?? []) {
          if (affected.has(`${g.studentId}::${skillId}::${type}`)) return true;
        }
      }
      return false;
    }
    for (const td of g.targetData ?? []) {
      for (const t of td.targets ?? []) {
        if (affected.has(`${g.studentId}::${td.skillId}::${t.type}`)) return true;
      }
    }
    return false;
  }

  /** Resets the cached index so the next measurement-driven run does a full rebuild. */
  private resetMeasurementIndexCache(): void {
    this._measurementIndex = null;
    this._lastMeasurementsSnapshot = [];
  }

  // =============================================
  // LIFECYCLE & INITIALIZATION
  // =============================================

  async initialize(config: MyGoalsInitConfig): Promise<void> {
    if (this._isInitializing) {
      console.warn('[MyGoalsState] Initialization already in progress');
      return;
    }

    this._isInitializing = true;
    this._loading.set(true);
    this._error.set(null);
    this._activeTeamId.set(config.activeTeamId);
    this._caseload.set(config.caseload);

    try {
      // Load from active team + any additional teams in parallel
      const [activeGoals, ...additionalGoalBatches] = await Promise.all([
        this.loadGoals(config.activeTeamId, config.caseload),
        ...(config.additionalTeamContexts ?? []).map(ctx => this.loadGoals(ctx.teamId, ctx.caseload)),
      ]);

      const allGoals = [activeGoals, ...additionalGoalBatches].flat();

      // Build the index once and keep it as the cache so the subsequent
      // measurement-driven effect can apply diffs instead of rebuilding.
      const measurements = this.myObservationsState.data();
      const index = buildMeasurementIndex(measurements) as MutableMeasurementIndex;
      this._measurementIndex = index;
      this._lastMeasurementsSnapshot = measurements;

      const skillNameMap = this.skillsState.nameMap();
      const studentNameMap = this.myStudentsState.nameMap();
      this._data.set(allGoals.map(goal => augmentGoal(goal, index, skillNameMap, studentNameMap)));
      this._initialized.set(true);
      this._lastUpdated.set(Date.now());
      this._lastSync.set(Date.now());

      if (config.enableRealtimeUpdates !== false) {
        this.startRealtimeListener(config.activeTeamId, config.caseload);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
      this._error.set(errorMessage);
      console.error('[MyGoalsState] ❌ Initialization failed:', error);
      throw error;
    } finally {
      this._loading.set(false);
      this._isInitializing = false;
    }
  }

  /**
   * Optimistically remove all goals belonging to a student from local state.
   * Call this immediately after a student is deleted so the UI clears before
   * the Firestore real-time listener catches up.
   */
  removeByStudentId(studentId: string): void {
    this._data.update(goals => goals.filter(g => g.studentId !== studentId));
  }

  reset(): void {
    this.stopRealtimeListener();

    if (this._effectDebounceTimer) {
      clearTimeout(this._effectDebounceTimer);
      this._effectDebounceTimer = null;
    }
    if (this._measurementApplyTimer) {
      clearTimeout(this._measurementApplyTimer);
      this._measurementApplyTimer = null;
    }
    this.resetMeasurementIndexCache();

    this._listenerMergeQueue = [];
    this._isProcessingMerge = false;

    this._data.set(MY_GOALS_DEFAULTS.data);
    this._initialized.set(MY_GOALS_DEFAULTS.initialized);
    this._loading.set(MY_GOALS_DEFAULTS.loading);
    this._error.set(MY_GOALS_DEFAULTS.error);
    this._lastUpdated.set(MY_GOALS_DEFAULTS.lastUpdated);
    this._lastSync.set(MY_GOALS_DEFAULTS.lastSync);
    this._activeTeamId.set(MY_GOALS_DEFAULTS.activeTeamId);
    this._caseload.set(MY_GOALS_DEFAULTS.caseload);
    this._isInitializing = false;
    this._activeProfileId = null;
  }

  // =============================================
  // PRIVATE METHODS (Helpers)
  // =============================================

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  /**
   * Returns the current MeasurementIndex — the cached incremental one if it
   * exists, otherwise builds a fresh one from the current measurements. The
   * cache is kept in sync by `applyMeasurementChange` and invalidated on
   * `reset()`, so callers in the realtime merge paths can reuse it safely.
   */
  private buildIndex(): MeasurementIndex {
    if (this._measurementIndex) return this._measurementIndex;
    const measurements = this.myObservationsState.data();
    const fresh = buildMeasurementIndex(measurements) as MutableMeasurementIndex;
    this._measurementIndex = fresh;
    this._lastMeasurementsSnapshot = measurements;
    return fresh;
  }

  // =============================================
  // PRIVATE METHODS (Data Loading)
  // =============================================

  private async loadGoals(activeTeamId: string, caseload: string[]): Promise<Goal[]> {
    if (caseload.length === 0) {
      return [];
    }

    const collectionPath = `partners/${activeTeamId}/goals`;
    const batches = this.chunkArray(caseload, 10);

    try {
      const allGoals: Goal[] = [];

      for (const batch of batches) {
        const batchGoals = await this.firestoreService.getCollectionAsync<Goal>(collectionPath, {
          where: [{ field: 'studentId', operator: 'in', value: batch }],
        });
        allGoals.push(...batchGoals);
      }

      return allGoals;
    } catch (error) {
      console.error('[MyGoalsState] Failed to load goals:', error);
      throw error;
    }
  }

  private mergeGoals(newGoals: Goal[]): void {
    const currentData = this._data();
    const caseload = this._caseload();

    const dataMap = new Map(currentData.map(g => [g.id, g]));
    const index = this.buildIndex();
    const skillNameMap = this.skillsState.nameMap();
    const studentNameMap = this.myStudentsState.nameMap();

    newGoals.forEach(goal => {
      if (goal.id) {
        dataMap.set(goal.id, augmentGoal(goal, index, skillNameMap, studentNameMap));
      }
    });

    const filtered = Array.from(dataMap.values()).filter(g => g.studentId && caseload.includes(g.studentId));

    this._data.set(filtered);
  }

  // =============================================
  // PRIVATE METHODS (Real-time Listener)
  // =============================================

  private async queueListenerUpdate(batchIndex: number, goals: Goal[]): Promise<void> {
    this._listenerMergeQueue.push({ batchIndex, goals });

    if (!this._isProcessingMerge) {
      await this.processListenerMergeQueue();
    }
  }

  private async processListenerMergeQueue(): Promise<void> {
    if (this._isProcessingMerge) return;

    this._isProcessingMerge = true;

    try {
      while (this._listenerMergeQueue.length > 0) {
        const update = this._listenerMergeQueue.shift();
        if (!update) continue;

        const { batchIndex, goals } = update;
        const caseload = this._caseload();

        const dataMap = new Map(this._data().map(g => [g.id, g]));
        const index = this.buildIndex();
        const skillNameMap = this.skillsState.nameMap();
        const studentNameMap = this.myStudentsState.nameMap();

        goals.forEach(goal => {
          if (goal.studentId && caseload.includes(goal.studentId)) {
            dataMap.set(goal.id, augmentGoal(goal, index, skillNameMap, studentNameMap));
          }
        });

        this._data.set(Array.from(dataMap.values()));
        this._lastUpdated.set(Date.now());
        this._lastSync.set(Date.now());

        await new Promise(resolve => setTimeout(resolve, 0));
      }
    } finally {
      this._isProcessingMerge = false;
    }
  }

  private startRealtimeListener(activeTeamId: string, caseload: string[]): void {
    if (caseload.length === 0) {
      console.warn('[MyGoalsState] Cannot start listener — empty caseload');
      return;
    }

    this.stopRealtimeListener();

    const collectionPath = `partners/${activeTeamId}/goals`;
    const batches = this.chunkArray(caseload, 10);

    batches.forEach((batch, batchIndex) => {
      const unsubscribe = this.firestoreService.onCollectionChange<Goal>(
        collectionPath,
        (batchGoals: Goal[]) => {
          if (batches.length === 1) {
            // Single batch: direct set — no race condition possible
            const index = this.buildIndex();
            const skillNameMap = this.skillsState.nameMap();
            const studentNameMap = this.myStudentsState.nameMap();
            const filtered = batchGoals
              .filter(g => g.studentId && caseload.includes(g.studentId))
              .map(goal => augmentGoal(goal, index, skillNameMap, studentNameMap));
            this._data.set(filtered);
            this._lastUpdated.set(Date.now());
            this._lastSync.set(Date.now());
          } else {
            // Multiple batches: queue to prevent race conditions
            this.queueListenerUpdate(batchIndex, batchGoals);
          }
        },
        {
          where: [{ field: 'studentId', operator: 'in', value: batch }],
        },
      );

      this._realtimeUnsubscribes.push(unsubscribe);
    });
  }

  private stopRealtimeListener(): void {
    if (this._realtimeUnsubscribes.length > 0) {
      this._realtimeUnsubscribes.forEach(u => u());
      this._realtimeUnsubscribes = [];

      this._listenerMergeQueue = [];
      this._isProcessingMerge = false;
    }
  }
}
